/**
 * Tasker Profile API — Supabase
 */

import { supabase, TABLES } from './db';
import { TaskerProfile, TaskerMetaForm, AiBuilderShowcase } from '../types';

const emptyAiShowcase: AiBuilderShowcase = {
  publicTasksStory: '',
  aiToolExperiences: [],
  featuredLinks: [],
};

// ============================================================================
// GET
// ============================================================================

export async function getTaskerProfileByUserId(userId: string): Promise<TaskerProfile | null> {
  // Guard: a falsy id serializes to `id=eq.undefined`, which Postgres rejects
  // as an invalid uuid (400) — that 400 used to throw and break whole pages.
  if (!userId) return null;
  const { data, error } = await supabase
    .from(TABLES.TASKER_PROFILES)
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return mapProfileFromDb(data);
}

export async function getTaskerProfile(profileId: string): Promise<TaskerProfile | null> {
  if (!profileId) return null;
  const { data, error } = await supabase
    .from(TABLES.TASKER_PROFILES)
    .select('*')
    .eq('id', profileId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return mapProfileFromDb(data);
}

export async function getAllTaskerProfiles(
  filters?: { status?: string; isTaskAchieverEligible?: boolean }
): Promise<TaskerProfile[]> {
  let q = supabase.from(TABLES.TASKER_PROFILES).select('*');
  if (filters?.status) q = q.eq('status', filters.status);
  if (filters?.isTaskAchieverEligible !== undefined)
    q = q.eq('is_task_achiever_eligible', filters.isTaskAchieverEligible);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map(mapProfileFromDb);
}

// Map snake_case DB row to camelCase TaskerProfile (jsonb columns stay as-is)
function mapProfileFromDb(row: Record<string, unknown>): TaskerProfile {
  return {
    id: row['id'] as string,
    userId: row['user_id'] as string,
    userEmail: (row['user_email'] as string) ?? '',
    status: (row['status'] as TaskerProfile['status']) ?? 'incomplete',
    taskAchieverStatus: (row['task_achiever_status'] as TaskerProfile['taskAchieverStatus']) ?? 'not-eligible',
    completionPercentage: (row['completion_percentage'] as number) ?? 0,
    isTaskAchieverEligible: (row['is_task_achiever_eligible'] as boolean) ?? false,
    personalInfo: (row['personal_info'] as TaskerProfile['personalInfo']) ?? {
      firstName: '', lastName: '', displayName: '',
    },
    professionalInfo: (row['professional_info'] as TaskerProfile['professionalInfo']) ?? {
      title: '', experienceLevel: 'entry', yearsOfExperience: 0,
    },
    skills: (row['skills'] as TaskerProfile['skills']) ?? {
      primary: [], secondary: [], certifications: [], languages: [],
    },
    workPreferences: (row['work_preferences'] as TaskerProfile['workPreferences']) ?? {
      availability: 'on-demand', location: 'remote', fastTrackAvailable: false,
    },
    specialties: (row['specialties'] as string[]) ?? [],
    services: (row['services'] as TaskerProfile['services']) ?? [],
    aiBuilderShowcase: (row['ai_builder_showcase'] as AiBuilderShowcase | undefined) ?? undefined,
    portfolio: (row['portfolio'] as TaskerProfile['portfolio']) ?? {
      projects: [], achievements: [], testimonials: [],
    },
    metrics: (row['metrics'] as TaskerProfile['metrics']) ?? {
      tasksCompleted: 0, tasksInProgress: 0, averageRating: 0, totalRatings: 0,
      slaReliability: 0, averageTurnaroundHours: 0, qualityScore: 0, responseTimeHours: 0,
    },
    taskAchieverCriteria: (row['task_achiever_criteria'] as TaskerProfile['taskAchieverCriteria']) ?? {
      minimumTasksCompleted: 10, minimumRating: 4.5, minimumSlaReliability: 95,
      minimumCompletionRate: 90, verifiedSkills: [], certificationsRequired: [], isVerified: false,
    },
    createdAt: (row['created_at'] as string) ?? new Date().toISOString(),
    updatedAt: (row['updated_at'] as string) ?? new Date().toISOString(),
    lastActiveAt: (row['last_active_at'] as string) ?? new Date().toISOString(),
    profileCompletedAt: row['profile_completed_at'] as string | undefined,
    taskAchieverApprovedAt: row['task_achiever_approved_at'] as string | undefined,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

function calculateCompletionPercentage(formData: TaskerMetaForm): number {
  let completed = 0;
  const total = 20;
  if (formData.personalInfo.firstName) completed++;
  if (formData.personalInfo.lastName) completed++;
  if (formData.personalInfo.displayName) completed++;
  if (formData.personalInfo.phone) completed++;
  if (formData.personalInfo.location) completed++;
  if (formData.personalInfo.timezone) completed++;
  if (formData.professionalInfo.title) completed++;
  if (formData.professionalInfo.experienceLevel) completed++;
  if (formData.professionalInfo.yearsOfExperience) completed++;
  if (formData.professionalInfo.currentRole) completed++;
  if (formData.professionalInfo.linkedInUrl) completed++;
  if (formData.professionalInfo.portfolioUrl) completed++;
  if (formData.skills.primary.length > 0) completed++;
  if (formData.skills.certifications.length > 0 || formData.skills.secondary.length > 0) completed++;
  if (formData.workPreferences.availability) completed++;
  if (formData.workPreferences.location) completed++;
  if (formData.workPreferences.hourlyRate) completed++;
  if (formData.workPreferences.currency) completed++;
  if (formData.specialties.length > 0) completed++;
  if (formData.portfolio.projects.length > 0) completed++;
  return Math.round((completed / total) * 100);
}

function checkTaskAchieverEligibility(
  formData: TaskerMetaForm,
  existingMetrics?: TaskerProfile['metrics']
): { isEligible: boolean; criteria: TaskerProfile['taskAchieverCriteria'] } {
  const criteria = {
    minimumTasksCompleted: 10,
    minimumRating: 4.5,
    minimumSlaReliability: 95,
    minimumCompletionRate: 90,
    verifiedSkills: formData.skills.primary.slice(0, 3),
    certificationsRequired: [] as string[],
    isVerified: false,
  };

  const metrics = existingMetrics ?? {
    tasksCompleted: 0, tasksInProgress: 0, averageRating: 0, totalRatings: 0,
    slaReliability: 0, averageTurnaroundHours: 0, qualityScore: 0, responseTimeHours: 0,
  };

  const completionPercentage = calculateCompletionPercentage(formData);
  const isEligible =
    metrics.tasksCompleted >= criteria.minimumTasksCompleted &&
    (metrics.totalRatings === 0 || metrics.averageRating >= criteria.minimumRating) &&
    metrics.slaReliability >= criteria.minimumSlaReliability &&
    completionPercentage >= 90 &&
    formData.skills.primary.length >= 3;

  return { isEligible, criteria };
}

// ============================================================================
// CREATE / UPDATE
// ============================================================================

export async function saveTaskerProfile(
  userId: string,
  userEmail: string,
  formData: TaskerMetaForm,
  existingProfile?: TaskerProfile
): Promise<string> {
  let profile: TaskerProfile | undefined = existingProfile;
  if (!profile) {
    try { profile = (await getTaskerProfileByUserId(userId)) ?? undefined; } catch { /* ignore */ }
  }

  const completionPercentage = calculateCompletionPercentage(formData);
  const eligibility = checkTaskAchieverEligibility(formData, profile?.metrics);
  const now = new Date().toISOString();

  const availabilitySchedule = {
    timezone: formData.workPreferences.timezone || 'UTC',
    days: formData.workPreferences.schedule,
  };

  const portfolio = {
    projects: formData.portfolio.projects.map((p, i) => ({
      ...p,
      id: (p as any).id || profile?.portfolio?.projects?.[i]?.id || `${Date.now()}-${i}`,
      isPublic: true,
      completedAt: p.completedAt || new Date(),
    })),
    achievements: formData.portfolio.achievements.map((a, i) => ({
      ...a,
      id: (a as any).id || profile?.portfolio?.achievements?.[i]?.id || `${Date.now()}-${i}`,
      date: a.date || new Date(),
    })),
    testimonials: profile?.portfolio?.testimonials || [],
  };

  const services = formData.services.map((s, i) => ({
    ...s,
    id: (s as any).id || profile?.services?.[i]?.id || `${Date.now()}-${i}`,
    isActive: true,
  }));

  const dbRow = {
    id: userId,
    user_id: userId,
    user_email: userEmail,
    status: completionPercentage >= 80 ? 'pending' : 'incomplete',
    task_achiever_status: eligibility.isEligible ? 'eligible' : 'not-eligible',
    completion_percentage: completionPercentage,
    is_task_achiever_eligible: eligibility.isEligible,
    personal_info: formData.personalInfo,
    professional_info: formData.professionalInfo,
    skills: {
      ...formData.skills,
      certifications: formData.skills.certifications.map((c) => ({
        ...c,
        id: `${Date.now()}-${Math.random()}`,
        verified: false,
      })),
    },
    work_preferences: { ...formData.workPreferences, availabilitySchedule, minimumTaskBudget: formData.workPreferences.minimumTaskBudget || 0 },
    specialties: formData.specialties,
    services,
    ai_builder_showcase: formData.aiBuilderShowcase
      ? { ...emptyAiShowcase, ...formData.aiBuilderShowcase }
      : profile?.aiBuilderShowcase || emptyAiShowcase,
    portfolio,
    metrics: profile?.metrics ?? {
      tasksCompleted: 0, tasksInProgress: 0, averageRating: 0, totalRatings: 0,
      slaReliability: 0, averageTurnaroundHours: 0, qualityScore: 0, responseTimeHours: 0,
    },
    task_achiever_criteria: eligibility.criteria,
    last_active_at: now,
    updated_at: now,
    ...(completionPercentage >= 80 && !profile?.profileCompletedAt ? { profile_completed_at: now } : {}),
    ...(!profile ? { created_at: now } : {}),
  };

  const { error } = await supabase
    .from(TABLES.TASKER_PROFILES)
    .upsert(dbRow, { onConflict: 'id' });
  if (error) throw error;

  return userId;
}

export async function updateTaskerProfileStatus(
  profileId: string,
  status: TaskerProfile['status']
): Promise<void> {
  const { error } = await supabase
    .from(TABLES.TASKER_PROFILES)
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', profileId);
  if (error) throw error;
}

export async function updateTaskAchieverStatus(
  profileId: string,
  status: TaskerProfile['taskAchieverStatus']
): Promise<void> {
  const updates: Record<string, unknown> = {
    task_achiever_status: status,
    updated_at: new Date().toISOString(),
  };
  if (status === 'approved') updates['task_achiever_approved_at'] = new Date().toISOString();
  const { error } = await supabase.from(TABLES.TASKER_PROFILES).update(updates).eq('id', profileId);
  if (error) throw error;
}

export async function updateTaskerMetrics(
  profileId: string,
  metrics: Partial<TaskerProfile['metrics']>
): Promise<void> {
  const { error } = await supabase
    .from(TABLES.TASKER_PROFILES)
    .update({ metrics, last_active_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', profileId);
  if (error) throw error;
}
